import React, { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material'
import {
  Backup as BackupIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { backupsApi, BackupConfiguracao, BackupConfiguracaoCreate } from '../api/backups'
import { empresasApi } from '../api/empresas'
import BackupsTable from '../components/Backups/BackupsTable'
import BackupForm from '../components/Backups/BackupForm'
import ExecucoesDialog from '../components/Backups/ExecucoesDialog'
import { sortByLocale } from '../utils/array'
import ExportButton from '../components/ExportButton'
import AddButton from '../components/AddButton'
import ActionBar from '../components/ActionBar'

const BackupsPage: React.FC = () => {
  const queryClient = useQueryClient()
  
  // Memoizar colunas do ExportButton para evitar re-renders
  const exportColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'nome_configuracao', label: 'Nome Configuração' },
    { key: 'id_empresa', label: 'ID Empresa' },
    { key: 'tipo_backup', label: 'Tipo Backup' },
    { key: 'frequencia', label: 'Frequência' },
    { key: 'ativo', label: 'Ativo', format: (v: any) => v ? 'Sim' : 'Não' },
  ], [])
  const [formOpen, setFormOpen] = useState(false)
  const [execucoesDialogOpen, setExecucoesDialogOpen] = useState(false)
  const [selectedBackup, setSelectedBackup] = useState<BackupConfiguracao | null>(null)
  const [empresaFiltro, setEmpresaFiltro] = useState<'all' | 'root' | number>('all')
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Buscar empresas
  const { data: empresas = [] } = useQuery({
    queryKey: ['empresas'],
    queryFn: () => empresasApi.list(),
  })

  const empresasOrdenadas = useMemo(
    () => sortByLocale(empresas, (empresa) => empresa.nome_display ?? empresa.nome ?? ''),
    [empresas],
  )

  const backupsQueryKey: (string | number | { id_empresa: number | null })[] = [
    'backups',
    {
      id_empresa:
        empresaFiltro === 'all' || empresaFiltro === 'root' ? null : (empresaFiltro as number),
    },
  ]

  // Buscar configurações de backup
  const {
    data: backupsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: backupsQueryKey,
    queryFn: () =>
      backupsApi.list({
        page_size: 100,
        ...(empresaFiltro === 'all' || empresaFiltro === 'root'
          ? {}
          : { id_empresa: empresaFiltro }),
      }),
  })

  const backups = backupsData?.backups || []

  // Buscar resumo de backups para dashboard
  const { data: resumo } = useQuery({
    queryKey: ['backups', 'resumo'],
    queryFn: () => backupsApi.getResumo(),
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  })

  // Função para formatar bytes
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  // Função para formatar data
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR')
  }

  // Mutação para criar backup
  const createMutation = useMutation({
    mutationFn: (data: BackupConfiguracaoCreate) => backupsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      setSnackbar({
        open: true,
        message: 'Configuração de backup criada com sucesso',
        severity: 'success',
      })
      setFormOpen(false)
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao criar configuração de backup',
        severity: 'error',
      })
    },
  })

  // Mutação para atualizar backup
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BackupConfiguracaoCreate> }) =>
      backupsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      setSnackbar({
        open: true,
        message: 'Configuração de backup atualizada com sucesso',
        severity: 'success',
      })
      setFormOpen(false)
      setSelectedBackup(null)
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao atualizar configuração de backup',
        severity: 'error',
      })
    },
  })

  // Mutação para deletar backup
  const deleteMutation = useMutation({
    mutationFn: (id: number) => backupsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      setSnackbar({
        open: true,
        message: 'Configuração de backup deletada com sucesso',
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao deletar configuração de backup',
        severity: 'error',
      })
    },
  })

  // Mutação para executar backup
  const executarMutation = useMutation({
    mutationFn: (id: number) => backupsApi.executar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      setSnackbar({
        open: true,
        message: 'Backup executado com sucesso',
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao executar backup',
        severity: 'error',
      })
    },
  })

  const handleCreate = () => {
    setSelectedBackup(null)
    setFormOpen(true)
  }

  const handleEdit = (backup: BackupConfiguracao) => {
    setSelectedBackup(backup)
    setFormOpen(true)
  }

  const handleDelete = (backup: BackupConfiguracao) => {
    if (window.confirm(`Deseja realmente deletar a configuração "${backup.nome_configuracao}"?`)) {
      deleteMutation.mutate(backup.id)
    }
  }

  const handleExecutar = (backup: BackupConfiguracao) => {
    if (window.confirm(`Deseja executar o backup "${backup.nome_configuracao}" agora?`)) {
      executarMutation.mutate(backup.id)
    }
  }

  const handleVerExecucoes = (backup: BackupConfiguracao) => {
    setSelectedBackup(backup)
    setExecucoesDialogOpen(true)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setSelectedBackup(null)
  }

  const handleCloseExecucoes = () => {
    setExecucoesDialogOpen(false)
    setSelectedBackup(null)
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 3 } }}>
        <Alert severity="error">Erro ao carregar backups: {String(error)}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 3 } }}>
        <ActionBar>
          <ExportButton
            data={Array.isArray(backups) ? backups : []}
            columns={exportColumns}
            filename="backups.xlsx"
            sheetName="Backups"
          />
          <AddButton onClick={handleCreate} title="Nova Configuração" />
        </ActionBar>

        {/* Dashboard de KPIs */}
        {resumo && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BackupIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      Total de Backups
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" color="primary">
                    {resumo.total_backups}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configurações cadastradas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      Backups Ativos
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" color="success.main">
                    {resumo.backups_ativos}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {resumo.total_backups > 0
                      ? `${Math.round((resumo.backups_ativos / resumo.total_backups) * 100)}% do total`
                      : '0% do total'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StorageIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      Armazenamento Total
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" color="info.main">
                    {formatBytes(resumo.total_size_bytes)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Em backups S3
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ErrorIcon color={resumo.backups_falhados > 0 ? 'error' : 'disabled'} sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      Backups Falhados
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    component="div"
                    color={resumo.backups_falhados > 0 ? 'error.main' : 'text.disabled'}
                  >
                    {resumo.backups_falhados}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requerem atenção
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {(resumo.backups_expirados > 0 || resumo.proximo_backup_expira) && (
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningIcon color={resumo.backups_expirados > 0 ? 'warning' : 'disabled'} sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Backups Expirados
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      component="div"
                      color={resumo.backups_expirados > 0 ? 'warning.main' : 'text.disabled'}
                    >
                      {resumo.backups_expirados}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {resumo.proximo_backup_expira
                        ? `Próximo expira: ${formatDate(resumo.proximo_backup_expira)}`
                        : 'Nenhum próximo'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        <Paper>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel id="empresa-filter-label">Empresa</InputLabel>
                  <Select
                    labelId="empresa-filter-label"
                    label="Empresa"
                    value={empresaFiltro}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === 'all' || value === 'root') {
                        setEmpresaFiltro(value)
                      } else {
                        setEmpresaFiltro(Number(value))
                      }
                    }}
                  >
                    <MenuItem value="all">Todas as empresas</MenuItem>
                    <MenuItem value="root">Sem empresa (galatea_web)</MenuItem>
                    {empresasOrdenadas.map((empresa) => (
                      <MenuItem key={empresa.id} value={empresa.id}>
                        {empresa.nome_display || empresa.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <BackupsTable
            backups={backups}
            empresas={empresas}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onExecutar={handleExecutar}
            onVerExecucoes={handleVerExecucoes}
          />
        </Paper>

        {formOpen && (
          <BackupForm
            open={formOpen}
            backup={selectedBackup}
            empresas={empresas}
            onClose={handleCloseForm}
            onSubmit={(data) => {
              if (selectedBackup) {
                updateMutation.mutate({ id: selectedBackup.id, data })
              } else {
                createMutation.mutate(data as BackupConfiguracaoCreate)
              }
            }}
          />
        )}

        {execucoesDialogOpen && selectedBackup && (
          <ExecucoesDialog
            open={execucoesDialogOpen}
            backup={selectedBackup}
            onClose={handleCloseExecucoes}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  )
}

export default BackupsPage

